import React, {useCallback, useMemo, useState}  from "react"
import {Avatar, Box, IconButton, Typography, useTheme} from "@mui/material"
import {BugReport}                             from "@mui/icons-material"
import Score                                    from "./Score"
import StudyScreenActions                       from "./StudyScreenActions"
import Confetti                                 from "../Confetti"
import StreamingMarkdown                        from "../prepkit/chat/StreamingMarkdown"
import {usePromptDebug}                         from "../debug/PromptDebugProvider"

// 75%: the same bar topicCardMeta uses on the dashboard and the daily challenge ladder tops out at
const PASSING = 0.75

/*
 * One stat under the score. The value is the thing the eye lands on, the label sits under it —
 * the same anatomy as the dashboard's HUD cards.
 */
const StatTile = ({custom, label, value, color}) => (
	<Box sx={{
		flex: '1 1 0',
		minWidth: 88,
		textAlign: 'center',
		background: custom.bg.cardInner,
		border: `1px solid ${custom.border.color}`,
		borderRadius: `${custom.radius.md}px`,
		py: 1.5,
		px: 1,
	}}>
		<Typography sx={{fontSize: 22, fontWeight: custom.fontWeight.bold, color, lineHeight: 1.2}}>
			{value}
		</Typography>
		<Typography sx={{
			mt: 0.25,
			fontSize: custom.fontSize.micro,
			fontWeight: custom.fontWeight.semibold,
			textTransform: 'uppercase',
			letterSpacing: .6,
			color: custom.text.muted,
		}}>
			{label}
		</Typography>
	</Box>
)

const TopicScore = ({topic, aiMessage, onContinue, onDashboard}) => {
	/*
	 * Prompt debug (097/099). This summary does not render through Message.js, which is where the
	 * debug button was originally added -- so it needs its own. The capture id arrives on the
	 * response header and QuestionView hangs it on the message.
	 */
	const promptDebug = usePromptDebug()

	const theme = useTheme()
	const custom = theme.custom

	const [celebrate, setCelebrate] = useState(false)

	const score = topic ? topic.percentCorrect : 0
	const passed = useMemo(() => typeof score === 'number' && score >= PASSING, [score])

	/*
	 * Fired by Score when the count-up settles, so the burst lands with the final number
	 * instead of going off while it is still climbing.
	 */
	const handleScoreSettled = useCallback(() => {
		if (passed) setCelebrate(true)
	}, [passed])

	const confettiColors = useMemo(() => [
		custom.status.green,
		custom.indigo.primary,
		custom.hud.streak,
		custom.hud.perf,
		custom.status.greenStrong,
	], [custom])

	// the counts behind the percentage already on screen (percentCorrect = correct / questions)
	const questionCount = (topic && topic.questionCount) || 0
	const correctCount = (topic && topic.correctCount) || 0
	const incorrectCount = (topic && topic.incorrectCount) || 0
	const answeredCount = (topic && topic.answeredCount) || 0

	/*
	 * Accuracy for this session: correct over ANSWERED (101). It is the same number the prompt is
	 * given as {accuracy_percent}, so the screen and Birdsy's summary state one figure rather than
	 * two that could differ on a topic with a skipped question.
	 */
	const accuracy = answeredCount ? Math.round((correctCount / answeredCount) * 100) : 0

	/*
	 * Where this topic sits: "Product · Section". topic.path is "/Section/Topic", the same source
	 * the question screen uses for its breadcrumb, and the same one the prompt is given.
	 */
	const trail = useMemo(() => {
		const parts = String((topic && topic.path) || '').split('/').filter(p => p)
		return parts.slice(0, -1)
	}, [topic])

	// actions appear with the feedback, once Birdsy has finished speaking
	const showActions = aiMessage && aiMessage.streaming === false

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			maxWidth="800px"
			width="100%"
			alignSelf="center"
			px={2}
			pt={4}
			pb={6}
		>
			<Confetti fire={celebrate} colors={confettiColors}/>

			{/* no panel around this block (078) — score, badge and title sit on the screen */}
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					/*
					 * Breathing room above the score ring (197). On the wrapper rather than on <Score>:
					 * the contained variant does not apply props.sx -- only the inline pill does -- so an
					 * sx passed to the component would be silently dropped.
					 */
					pt: '30px',
				}}
			>
				<Score
					variant="contained"
					topic={topic}
					size={85}
					animate={true}
					onAnimationEnd={handleScoreSettled}
				/>

				{passed && (
					<Box sx={{
						mt: 2.5,
						px: 1.75,
						py: 0.625,
						borderRadius: 999,
						background: `${custom.status.green}22`,
						border: `1px solid ${custom.status.green}59`,
						fontSize: custom.fontSize.xs,
						fontWeight: custom.fontWeight.bold,
						textTransform: 'uppercase',
						letterSpacing: .6,
						color: custom.status.green,
					}}>
						🎉 Passing score!
					</Box>
				)}

				{/* where this sits in the course — the same trail the summary prompt is given (101) */}
				{trail.length > 0 && (
					<Typography sx={{
						mt: passed ? 2 : 3,
						fontSize: custom.fontSize.xs,
						fontWeight: custom.fontWeight.semibold,
						textTransform: 'uppercase',
						letterSpacing: '0.08em',
						color: custom.text.muted,
						textAlign: 'center',
					}}>
						{trail.join(' · ')}
					</Typography>
				)}

				<Typography sx={{
					mt: trail.length > 0 ? 0.75 : (passed ? 2 : 3),
					fontSize: {xs: 20, md: 24},
					fontWeight: custom.fontWeight.bold,
					color: custom.text.main,
					textAlign: 'center',
					lineHeight: 1.3,
				}}>
					{topic.name} Complete!
				</Typography>

				{/* the numbers behind the percentage above */}
				<Box sx={{display: 'flex', gap: 1.5, width: '100%', mt: 2.5, flexWrap: 'wrap'}}>
					<StatTile custom={custom} label="Questions" value={questionCount} color={custom.text.main}/>
					<StatTile custom={custom} label="Correct" value={correctCount} color={custom.status.green}/>
					{/* muted at zero: a clean sweep shouldn't show a red number */}
					<StatTile
						custom={custom}
						label="Incorrect"
						value={incorrectCount}
						color={incorrectCount > 0 ? custom.status.danger : custom.text.muted}
					/>
					{/* correct over ANSWERED — the figure the AI summary is told to honour */}
					<StatTile
						custom={custom}
						label="Score"
						value={`${accuracy}%`}
						color={accuracy >= 75 ? custom.status.green : accuracy >= 65 ? custom.status.amber : custom.status.danger}
					/>
				</Box>
			</Box>

			{/* Birdsy's feedback — recessed panel so it reads as the tutor speaking */}
			{topic && aiMessage && (
				<Box
					sx={{
						width: '100%',
						display: 'flex',
						alignItems: 'flex-start',
						gap: 2,
						background: custom.bg.cardInner,
						border: `1px solid ${custom.border.color}`,
						borderRadius: `${custom.radius.md}px`,
						p: 2.5,
						mt: 2.5,
						textAlign: 'left',
					}}
				>
					<Avatar
						src="/birdsy-avatar-black.png"
						sx={{
							flexShrink: 0,
							width: 32,
							height: 32,
							p: '2px',
							background: custom.bg.surface,
							border: `1px solid ${custom.border.strong}`,
							boxShadow: custom.shadow.card,
						}}
					/>
					<Box sx={{minWidth: 0, flex: 1, fontSize: custom.fontSize.md, color: custom.text.main}}>
						<StreamingMarkdown text={aiMessage.body} streaming={aiMessage.streaming}/>
					</Box>
					{promptDebug.enabled && aiMessage.debug_capture_id && (
						<IconButton
							size="small"
							title="What was sent to the model"
							onClick={() => promptDebug.openCapture(aiMessage.debug_capture_id)}
							sx={{flexShrink: 0, p: .25, opacity: .6, '&:hover': {opacity: 1}}}
						>
							<BugReport sx={{fontSize: 16}}/>
						</IconButton>
					)}
				</Box>
			)}

			{/*
			  * Inline, not a fixed footer (077). Two ways on from here: the next section/topic,
			  * or the dashboard. Styling matches the dashboard's primary and outlined buttons.
			  */}
			{showActions && (
				<Box sx={{mt: 3}}>
					<StudyScreenActions onContinue={onContinue} onDashboard={onDashboard}/>
				</Box>
			)}
		</Box>
	)
}

export default TopicScore
